import { useSyncExternalStore } from 'react';

/**
 * Minimal observable value for state that lives outside React (upload progress, the live room,
 * viewer preferences). Components read it through `useStoreValue`, which keeps rendering tear-free.
 */
export interface Store<T> {
  get(): T;
  set(next: T | ((current: T) => T)): void;
  subscribe(listener: () => void): () => void;
}

export const createStore = <T>(initial: T): Store<T> => {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      const resolved = typeof next === 'function' ? (next as (current: T) => T)(value) : next;
      if (Object.is(resolved, value)) return;
      value = resolved;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

export interface ReadableStore<T> {
  get(): T;
  subscribe(listener: () => void): () => void;
}

export const useStoreValue = <T>(store: ReadableStore<T>): T =>
  useSyncExternalStore(store.subscribe, store.get, store.get);
